import {
  Bell,
  Check,
  Loader2,
  MessageSquare,
  Plus,
  Ticket,
  UserCheck,
  LogIn,
  LogOut,
  Send,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { useCommunity } from "@/contexts/CommunityContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

function formatRelativeTime(date: Date | string) {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  const timeStr = then.toLocaleTimeString("es-AR", {
    hour: "numeric",
    minute: "2-digit",
  });

  if (diffMins < 1) return `Ahora · ${timeStr}`;
  if (diffMins < 60) return `${diffMins}m · ${timeStr}`;
  if (diffHours < 24) return `${diffHours}h · ${timeStr}`;

  const dateStr = then.toLocaleDateString("es-AR", {
    month: "short",
    day: "numeric",
  });

  if (diffDays < 7) return `${dateStr} · ${timeStr}`;

  return `${dateStr} · ${timeStr}`;
}

function getNotificationIcon(
  type: string | undefined,
  notificationType: string | undefined,
) {
  if (type === "notification") {
    switch (notificationType) {
      case "INVITATION_FILLED":
        return <UserCheck className="h-4 w-4" />;
      case "GUEST_ARRIVED":
        return <LogIn className="h-4 w-4" />;
      case "GUEST_DEPARTED":
        return <LogOut className="h-4 w-4" />;
      case "TICKET_STATUS_CHANGED":
        return <Ticket className="h-4 w-4" />;
      case "TICKET_COMMENT_ADDED":
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  }
  return <Bell className="h-4 w-4" />;
}

function getNotificationBadge(
  type: string | undefined,
  notificationType: string | undefined,
) {
  if (type === "communication") {
    return <Badge variant="secondary">Comunicado</Badge>;
  }
  switch (notificationType) {
    case "INVITATION_FILLED":
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-600">
          Invitado listo
        </Badge>
      );
    case "GUEST_ARRIVED":
      return (
        <Badge variant="outline" className="border-green-500 text-green-600">
          Ingresó
        </Badge>
      );
    case "GUEST_DEPARTED":
      return (
        <Badge variant="outline" className="border-orange-500 text-orange-600">
          Egresó
        </Badge>
      );
    case "TICKET_STATUS_CHANGED":
      return (
        <Badge variant="outline" className="border-purple-500 text-purple-600">
          Ticket actualizado
        </Badge>
      );
    case "TICKET_COMMENT_ADDED":
      return (
        <Badge variant="outline" className="border-purple-500 text-purple-600">
          Nuevo comentario
        </Badge>
      );
    default:
      return null;
  }
}

function SentCommunicationsTab({ communityId }: { communityId: string }) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.communication.getAllForCommunity.useInfiniteQuery(
      { communityId, limit: 20 },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
    );

  const communications = data?.pages.flatMap((p) => p.items) ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (communications.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Send className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">
            Todavía no enviaste ningún comunicado.
          </p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/communications/create">
              <Plus className="mr-2 h-4 w-4" />
              Enviá tu primer comunicado
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {communications.map((comm) => {
        const readPercent =
          comm.recipientCount > 0
            ? Math.round((comm.readCount / comm.recipientCount) * 100)
            : 0;

        return (
          <Card key={comm.id}>
            <CardHeader className="pb-2">
              <CardDescription className="mb-1">
                {formatRelativeTime(comm.createdAt)}
              </CardDescription>
              <CardTitle className="text-lg">{comm.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{comm.body}</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-sm">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {comm.readCount} de {comm.recipientCount}
                  </span>
                  <span className="text-muted-foreground">
                    {comm.recipientCount === 1 ? "leyó" : "leyeron"} (
                    {readPercent}%)
                  </span>
                </div>
                <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${readPercent}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {hasNextPage && (
        <div className="pt-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Cargar más
          </Button>
        </div>
      )}
    </div>
  );
}

function InboxTab({
  communityId,
  isAdmin,
}: {
  communityId: string;
  isAdmin: boolean;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.notification.getInbox.useInfiniteQuery(
      { communityId, limit: 20 },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
    );

  const markAsRead = trpc.notification.markAsRead.useMutation({
    onSuccess: () => {
      utils.notification.getUnreadCount.invalidate();
      utils.notification.getInbox.invalidate();
    },
  });

  const markAllAsRead = trpc.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.notification.getUnreadCount.invalidate();
      utils.notification.getInbox.invalidate();
    },
  });

  const notifications = data?.pages.flatMap((p) => p.items) ?? [];
  const hasUnread = notifications.some((n) => !n.readAt);

  const handleNotificationClick = (notification: {
    id: string;
    type: "communication" | "notification";
    readAt: Date | string | null;
    ticket?: { id: string } | null;
  }) => {
    if (!notification.readAt) {
      markAsRead.mutate({ id: notification.id, type: notification.type });
    }
    if (notification.ticket?.id) {
      navigate({
        to: "/tickets/$ticketId",
        params: { ticketId: notification.ticket.id },
      });
    }
  };

  return (
    <>
      {hasUnread && (
        <div className="mb-4 flex justify-end">
          <Button
            variant="outline"
            onClick={() => markAllAsRead.mutate({ communityId })}
            disabled={markAllAsRead.isPending}
          >
            {markAllAsRead.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            Marcar todo como leído
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">Tu bandeja está vacía.</p>
            {isAdmin && (
              <Button asChild variant="outline" className="mt-4">
                <Link to="/communications/create">
                  <Plus className="mr-2 h-4 w-4" />
                  Enviá tu primer comunicado
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              className={cn(
                "cursor-pointer transition-colors hover:bg-accent/50",
                !notification.readAt && "border-primary/20 bg-primary/5",
              )}
              onClick={() => handleNotificationClick(notification)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                  {!notification.readAt && (
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  )}
                  <div className={cn("flex-1", notification.readAt && "ml-5")}>
                    <CardDescription className="mb-1">
                      {formatRelativeTime(notification.createdAt)}
                      {notification.attachmentUrls.length > 0 && (
                        <>
                          {" · "}
                          {notification.attachmentUrls.length} adjunto
                          {notification.attachmentUrls.length > 1 ? "s" : ""}
                        </>
                      )}
                    </CardDescription>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        {getNotificationIcon(
                          notification.type,
                          notification.notificationType,
                        )}
                      </div>
                      {getNotificationBadge(
                        notification.type,
                        notification.notificationType,
                      )}
                    </div>
                    <CardTitle className="text-lg">
                      {notification.title}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p
                  className={cn(
                    "text-sm text-muted-foreground",
                    notification.readAt && "ml-5",
                  )}
                >
                  {notification.body}
                </p>
              </CardContent>
            </Card>
          ))}

          {hasNextPage && (
            <div className="pt-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Cargar más
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function NotificationsPage() {
  const { selectedCommunityId } = useCommunity();

  const { data: communities } = trpc.amenity.getUserCommunities.useQuery();

  const selectedCommunity = communities?.find(
    (c) => c.id === selectedCommunityId,
  );
  const isAdmin = selectedCommunity?.role === "COMMUNITY_ADMIN";

  if (!selectedCommunityId) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">Bandeja de entrada</h1>
          <p className="text-muted-foreground">
            Mantenete al día con las novedades del barrio.
          </p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              Seleccioná un barrio primero.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Bandeja de entrada</h1>
          <p className="text-muted-foreground">
            Mantenete al día con las novedades del barrio.
          </p>
        </div>
        {isAdmin && (
          <Button asChild>
            <Link to="/communications/create">
              <Plus className="mr-2 h-4 w-4" />
              Nuevo comunicado
            </Link>
          </Button>
        )}
      </div>

      {isAdmin ? (
        <Tabs defaultValue="inbox">
          <TabsList>
            <TabsTrigger value="inbox">
              <Bell className="mr-1.5 h-4 w-4" />
              Recibidos
            </TabsTrigger>
            <TabsTrigger value="sent">
              <Send className="mr-1.5 h-4 w-4" />
              Enviados
            </TabsTrigger>
          </TabsList>
          <TabsContent value="inbox">
            <InboxTab communityId={selectedCommunityId} isAdmin={isAdmin} />
          </TabsContent>
          <TabsContent value="sent">
            <SentCommunicationsTab communityId={selectedCommunityId} />
          </TabsContent>
        </Tabs>
      ) : (
        <InboxTab communityId={selectedCommunityId} isAdmin={isAdmin} />
      )}
    </div>
  );
}
